import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import toast from '../utils/toast';

const statusTone = (group) => {
  const status = String(group?.status || '').toUpperCase();
  if (group?.can_scan_now) {
    return { bg: '#f0fdf4', border: '#86efac', text: '#166534' };
  }
  if (status === 'ALREADY_USED') {
    return { bg: '#fffbeb', border: '#fde68a', text: '#92400e' };
  }
  if (status === 'WRONG_DAY') {
    return { bg: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8' };
  }
  return { bg: '#fef2f2', border: '#fecaca', text: '#991b1b' };
};

const ManualDashboardScanPanel = ({ enabled = false }) => {
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');

  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsRefreshing, setGroupsRefreshing] = useState(false);
  const [groups, setGroups] = useState([]);
  const [totals, setTotals] = useState(null);
  const [effectiveScanEventDate, setEffectiveScanEventDate] = useState('');

  const [commitLoadingGroupKey, setCommitLoadingGroupKey] = useState('');
  const [error, setError] = useState('');

  const selectedUser = useMemo(() => {
    if (!selectedUserId) return null;
    return users.find((user) => user.id === selectedUserId) || null;
  }, [users, selectedUserId]);

  const loadUsers = useCallback(async (searchValue) => {
    if (searchValue.length < 2) {
      setUsers([]);
      setSelectedUserId('');
      setGroups([]);
      setTotals(null);
      setError('');
      return;
    }

    setSearching(true);
    setError('');
    try {
      const response = await api.searchManualScanUsers(searchValue);
      const rows = response?.data || [];
      setUsers(rows);
      if (!rows.length) {
        setSelectedUserId('');
        setGroups([]);
        setTotals(null);
      }
    } catch (err) {
      setUsers([]);
      setSelectedUserId('');
      setGroups([]);
      setTotals(null);
      setError(err?.message || 'Could not search users right now.');
    } finally {
      setSearching(false);
    }
  }, []);

  const loadUserGroups = useCallback(async (userId, { silent = false } = {}) => {
    if (!userId) return;

    if (silent) {
      setGroupsRefreshing(true);
    } else {
      setGroupsLoading(true);
      setError('');
    }

    try {
      const response = await api.getManualScanUserTickets(
        userId,
        null,
      );
      const payload = response?.data || {};
      setGroups(payload.groups || []);
      setTotals(payload.totals || null);
      setEffectiveScanEventDate(payload.effective_scan_event_date || '');
    } catch (err) {
      if (!silent) {
        setGroups([]);
        setTotals(null);
        setError(err?.message || 'Could not load tickets for this user.');
      }
    } finally {
      if (silent) {
        setGroupsRefreshing(false);
      } else {
        setGroupsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    const trimmed = query.trim();
    const debounce = setTimeout(() => {
      loadUsers(trimmed);
    }, 220);

    return () => clearTimeout(debounce);
  }, [enabled, query, loadUsers]);

  useEffect(() => {
    if (!users.length) {
      setSelectedUserId('');
      return;
    }

    setSelectedUserId((previous) => {
      if (previous && users.some((row) => row.id === previous)) {
        return previous;
      }
      return users[0].id;
    });
  }, [users]);

  useEffect(() => {
    if (!enabled || !selectedUserId) {
      setGroups([]);
      setTotals(null);
      return;
    }
    loadUserGroups(selectedUserId);
  }, [enabled, selectedUserId, loadUserGroups]);

  useEffect(() => {
    if (!enabled || !selectedUserId) return undefined;

    const poll = setInterval(() => {
      loadUserGroups(selectedUserId, { silent: true });
    }, 5000);

    return () => clearInterval(poll);
  }, [enabled, selectedUserId, loadUserGroups]);

  const handleCommitGroup = useCallback(async (groupKey) => {
    if (!selectedUserId || !groupKey) return;

    setCommitLoadingGroupKey(groupKey);
    try {
      const response = await api.commitManualScanGroup(
        selectedUserId,
        groupKey,
        null,
      );
      const scannedCode = response?.data?.scanned_ticket_code;
      toast.success(scannedCode ? `Scanned ${scannedCode}` : 'Ticket scanned successfully');
      await loadUserGroups(selectedUserId);
    } catch (err) {
      toast.error(err?.message || 'Could not mark ticket as scanned.');
      await loadUserGroups(selectedUserId, { silent: true });
    } finally {
      setCommitLoadingGroupKey('');
    }
  }, [selectedUserId, loadUserGroups]);

  if (!enabled) return null;

  return (
    <div style={{
      marginTop: '1rem',
      padding: '1rem',
      borderRadius: '12px',
      border: '1px solid #e5e7eb',
      backgroundColor: '#ffffff',
    }}>
      <div style={{ marginBottom: '0.75rem' }}>
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#111827' }}>
          Manual Smart Scan
        </h3>
        <p style={{ margin: '0.25rem 0 0', fontSize: '0.84rem', color: '#6b7280' }}>
          Search by name, email, or phone. Multi-day pass groups are scanned as one action.
        </p>
      </div>

      <input
        type="text"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search attendee (first name, last name, email, phone)"
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: '0.65rem 0.75rem',
          borderRadius: '8px',
          border: '1px solid #d1d5db',
          fontSize: '0.9rem',
          marginBottom: '0.75rem',
        }}
      />

      <p style={{ margin: '0 0 0.75rem', color: '#6b7280', fontSize: '0.8rem' }}>
        Event day is auto-detected from today&apos;s date.
      </p>

      {searching && <p style={{ margin: '0 0 0.75rem', color: '#6b7280', fontSize: '0.82rem' }}>Searching users...</p>}

      {!searching && query.trim().length >= 2 && users.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
          {users.map((user) => {
            const isSelected = selectedUserId === user.id;
            return (
              <button
                key={user.id}
                type="button"
                onClick={() => setSelectedUserId(user.id)}
                style={{
                  textAlign: 'left',
                  border: isSelected ? '1px solid #7c3aed' : '1px solid #d1d5db',
                  backgroundColor: isSelected ? '#faf5ff' : '#ffffff',
                  borderRadius: '8px',
                  padding: '0.5rem 0.65rem',
                  cursor: 'pointer',
                  minWidth: '230px',
                }}
              >
                <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#111827' }}>{user.full_name || user.email}</div>
                <div style={{ fontSize: '0.74rem', color: '#6b7280' }}>{user.email}</div>
                <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>
                  {user.phone || 'No phone'} · {user.total_tickets || 0} tickets
                </div>
              </button>
            );
          })}
        </div>
      )}

      {!searching && query.trim().length >= 2 && users.length === 0 && !error && (
        <p style={{ margin: '0 0 0.75rem', color: '#6b7280', fontSize: '0.82rem' }}>No matching users found.</p>
      )}

      {error && (
        <p style={{
          margin: '0 0 0.75rem',
          color: '#991b1b',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          padding: '0.55rem 0.7rem',
          fontSize: '0.8rem',
        }}>
          {error}
        </p>
      )}

      {selectedUser && (
        <div style={{ marginBottom: '0.75rem', padding: '0.65rem', borderRadius: '8px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: '0.83rem', fontWeight: 700, color: '#111827' }}>
            {selectedUser.full_name || selectedUser.email}
          </div>
          <div style={{ fontSize: '0.76rem', color: '#6b7280' }}>
            {selectedUser.email} {selectedUser.phone ? `· ${selectedUser.phone}` : ''}
          </div>
          <div style={{ marginTop: '0.35rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem', fontSize: '0.74rem', color: '#4b5563' }}>
            <span>{totals?.groups_total || 0} groups</span>
            <span>{totals?.entries_used || 0} used</span>
            <span>{totals?.entries_remaining || 0} left</span>
            <span>{totals?.entries_available_now || 0} eligible now</span>
            {effectiveScanEventDate && <span>scan day: {effectiveScanEventDate}</span>}
            {groupsRefreshing && <span>refreshing...</span>}
          </div>
        </div>
      )}

      {groupsLoading && selectedUserId && (
        <p style={{ margin: 0, color: '#6b7280', fontSize: '0.82rem' }}>Loading tickets...</p>
      )}

      {!groupsLoading && selectedUserId && groups.length === 0 && (
        <p style={{ margin: 0, color: '#6b7280', fontSize: '0.82rem' }}>
          This user has no gate-scannable tickets.
        </p>
      )}

      {!groupsLoading && groups.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(255px, 1fr))', gap: '0.65rem' }}>
          {groups.map((group) => {
            const tone = statusTone(group);
            const isCommitting = commitLoadingGroupKey === group.group_key;
            const isFestivalGroup = group.group_kind === 'festival_pass_group';
            const actionLabel = isFestivalGroup ? 'Mark 1 Entry as Scanned' : 'Mark as Scanned';

            return (
              <div
                key={group.group_key}
                style={{
                  border: `1px solid ${tone.border}`,
                  backgroundColor: tone.bg,
                  borderRadius: '10px',
                  padding: '0.7rem',
                }}
              >
                <div style={{ fontSize: '0.88rem', fontWeight: 800, color: '#111827' }}>
                  {group.ticket_type_name}
                </div>

                {group.is_amphitheater && (
                  <div style={{ fontSize: '0.74rem', color: '#4b5563', marginTop: '0.1rem' }}>
                    {group.section || 'Section ?'} · Row {group.row || '?'} · Seat {group.seat || '?'}
                    {group.event_date ? ` · ${group.event_date}` : ''}
                  </div>
                )}

                <div style={{
                  marginTop: '0.45rem',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.4rem',
                  fontSize: '0.72rem',
                  color: '#374151',
                }}>
                  <span>Total: {group.entries_total}</span>
                  <span>Used: {group.entries_used}</span>
                  <span>Left: {group.entries_remaining}</span>
                  <span>Eligible now: {group.entries_available_now}</span>
                </div>

                <div style={{ marginTop: '0.45rem', fontSize: '0.74rem', fontWeight: 600, color: tone.text }}>
                  {group.message || 'No status message'}
                </div>

                <button
                  type="button"
                  onClick={() => handleCommitGroup(group.group_key)}
                  disabled={!group.can_scan_now || isCommitting}
                  style={{
                    marginTop: '0.55rem',
                    width: '100%',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '0.55rem 0.7rem',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    color: '#ffffff',
                    backgroundColor: group.can_scan_now ? '#16a34a' : '#9ca3af',
                    cursor: group.can_scan_now && !isCommitting ? 'pointer' : 'not-allowed',
                  }}
                >
                  {isCommitting ? 'Scanning...' : actionLabel}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ManualDashboardScanPanel;
